import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from '../../../environment/environment';
import { OrgSyncLogDialogComponent } from './org-sync-log-dialog.component';
import {
  OrgSyncUserErrorsDialogComponent,
  UserSyncErrorsPayload,
} from './org-sync-user-errors-dialog.component';
import {
  formatElapsed,
  phaseLabelDa,
  progressDetail,
} from './org-sync-phase.util';

export interface IdentitySyncOverviewRow {
  identity_types_id: number;
  organizations_id: number;
  identity_status: string;
  realm: string | null;
  org_name: string | null;
  last_run_id: number | null;
  last_run_status: string | null;
  last_run_started_at: string | null;
  last_run_finished_at: string | null;
  last_run_duration_ms: number | null;
  last_run_elapsed_ms: number | null;
  last_run_phase: string | null;
  last_run_last_user_index: number | null;
  last_run_users_after_cleanup: number | null;
  last_run_users_soft_deleted: number | null;
  last_run_trigger_source: string | null;
  last_run_users_success: number | null;
  last_run_users_errors: number | null;
  last_run_keycloak_users_count: number | null;
  last_run_error_message: string | null;
  last_run_log_tail: string | null;
  last_run_log_digest: string | null;
  last_run_file_log_tail: string | null;
  last_activity_line: string | null;
  phase_progress_percent: number | null;
  last_success_at: string | null;
  minutes_since_last_success: number | null;
  error_summary: string | null;
  sync_sla_ok: boolean | null;
  last_run_user_sync_errors?: UserSyncErrorsPayload | null;
  last_run_keycloak_fetch_ms?: number | null;
  last_run_keycloak_fetch_started_at?: string | null;
  last_run_keycloak_fetch_finished_at?: string | null;
  keycloak_fetch_summary?: string | null;
  sync_timing?: SyncTimingInfo | null;
}

export interface SyncTimingInfo {
  keycloak_ms: number | null;
  total_ms: number | null;
  after_keycloak_ms: number | null;
  lines: string[];
  summary: string;
}

@Component({
  selector: 'app-duna-cprng-orgsync',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatChipsModule,
    MatDialogModule,
    MatProgressBarModule,
  ],
  templateUrl: './duna-cprng-orgsync.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './duna-cprng-orgsync.component.scss',
})
export class DunaCprngOrgsyncComponent implements OnInit, OnDestroy {
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  loading = false;
  triggeringAll = false;
  resettingRunning = false;
  triggeringId: number | null = null;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  readonly phaseLabel = phaseLabelDa;
  readonly progressDetail = progressDetail;
  readonly formatElapsed = formatElapsed;

  tableDataSource = new MatTableDataSource<IdentitySyncOverviewRow>([]);
  displayedColumns = [
    'org_name',
    'last_run_status',
    'progress',
    'errors',
    'metrics',
    'timing',
    'actions',
  ];

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadOverview();
    this.refreshTimer = setInterval(() => {
      if (this.hasRunningRows()) {
        this.loadOverview(true);
      }
    }, 5000);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  }

  hasRunningRows(): boolean {
    return this.tableDataSource.data.some(
      (r) => r.last_run_status === 'running'
    );
  }

  loadOverview(silent = false): void {
    if (!silent) {
      this.loading = true;
    }
    this.errorMessage = null;
    this.http
      .get<IdentitySyncOverviewRow[]>(
        `${environment.apiUrl}/organizations/identity-sync/overview`
      )
      .subscribe({
        next: (rows) => {
          this.loading = false;
          this.tableDataSource.data = rows ?? [];
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke hente org sync-status.'
          );
          this.cdr.markForCheck();
        },
      });
  }

  elapsedForRow(row: IdentitySyncOverviewRow): string {
    if (row.last_run_status === 'running' && row.last_run_elapsed_ms != null) {
      return formatElapsed(row.last_run_elapsed_ms) + ' (igang)';
    }
    return this.formatDuration(row.last_run_duration_ms);
  }

  triggerSync(row: IdentitySyncOverviewRow): void {
    this.triggeringId = row.identity_types_id;
    this.successMessage = null;
    this.errorMessage = null;
    this.http
      .post(`${environment.apiUrl}/organizations/identity-sync/trigger`, {
        identity_types_id: row.identity_types_id,
      })
      .subscribe({
        next: () => {
          this.triggeringId = null;
          this.successMessage =
            'Sync sat i kø for ' +
            (row.org_name ?? row.realm ?? row.identity_types_id) +
            ' (kører via cron — opdater om ~1 min).';
          this.loadOverview();
        },
        error: (error) => {
          this.triggeringId = null;
          this.errorMessage = this.formatApiError(error, 'Kunne ikke starte sync.');
          this.cdr.markForCheck();
        },
      });
  }

  resetStuckRunning(): void {
    this.resettingRunning = true;
    this.errorMessage = null;
    this.http
      .post(`${environment.apiUrl}/organizations/identity-sync/reset-running`, {})
      .subscribe({
        next: () => {
          this.resettingRunning = false;
          this.successMessage = 'Hængende RUNNING-rækker er nulstillet.';
          this.loadOverview();
        },
        error: (error) => {
          this.resettingRunning = false;
          this.errorMessage = this.formatApiError(error, 'Kunne ikke nulstille.');
          this.cdr.markForCheck();
        },
      });
  }

  triggerSyncAll(): void {
    this.triggeringAll = true;
    this.successMessage = null;
    this.errorMessage = null;
    this.http
      .post(`${environment.apiUrl}/organizations/identity-sync/trigger`, {
        full_chain: true,
      })
      .subscribe({
        next: () => {
          this.triggeringAll = false;
          this.successMessage =
            'Fuld kæde sat i kø via cron-trigger (undgår HTTP-timeout). Opdater om ~1 min.';
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.triggeringAll = false;
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke starte fuld sync.'
          );
          this.cdr.markForCheck();
        },
      });
  }

  openUserErrors(row: IdentitySyncOverviewRow): void {
    if (!row.last_run_id) {
      return;
    }
    this.dialog.open(OrgSyncUserErrorsDialogComponent, {
      width: '92vw',
      maxWidth: '1000px',
      data: {
        title: `${row.org_name ?? 'Org'} / ${row.realm ?? 'realm'}`,
        runId: row.last_run_id,
        usersErrors: row.last_run_users_errors ?? 0,
        preloaded: row.last_run_user_sync_errors ?? null,
      },
    });
  }

  openLog(row: IdentitySyncOverviewRow): void {
    const tail = row.last_run_log_tail?.trim();
    const err = row.last_run_error_message?.trim();
    this.dialog.open(OrgSyncLogDialogComponent, {
      width: '90vw',
      maxWidth: '960px',
      data: {
        title: `${row.org_name ?? 'Org'} / ${row.realm ?? 'realm'}`,
        status: row.last_run_status,
        phase: row.last_run_phase,
        errorMessage: err,
        logTail: tail,
        fileLogTail: row.last_run_file_log_tail ?? null,
        logDigest: row.last_run_log_digest ?? null,
        identityTypesId: row.identity_types_id,
        syncTiming: row.sync_timing ?? null,
      },
    });
  }

  statusClass(status: string | null | undefined): string {
    if (!status) {
      return 'status-unknown';
    }
    return 'status-' + status.replace(/[^a-z0-9_-]/gi, '');
  }

  displayStatus(row: IdentitySyncOverviewRow): string {
    return row.last_run_status ?? 'ingen kørsel';
  }

  statusClassForRow(row: IdentitySyncOverviewRow): string {
    return this.statusClass(row.last_run_status);
  }

  rowClass(row: IdentitySyncOverviewRow): Record<string, boolean> {
    return {
      'row-running': row.last_run_status === 'running',
      'row-sla-miss': row.sync_sla_ok === false,
      'row-has-errors': (row.last_run_users_errors ?? 0) > 0 || !!row.error_summary,
    };
  }

  slaLabel(row: IdentitySyncOverviewRow): string {
    if (row.last_run_status === 'running') {
      return 'Sync kører';
    }
    if (row.minutes_since_last_success == null) {
      return 'Ingen vellykket sync endnu';
    }
    return `Sidste OK: ${row.minutes_since_last_success} min siden`;
  }

  formatDuration(ms: number | null | undefined): string {
    if (ms == null || ms < 0) {
      return '–';
    }
    return formatElapsed(ms);
  }

  progressPercent(row: IdentitySyncOverviewRow): number {
    if (row.phase_progress_percent != null) {
      return row.phase_progress_percent;
    }
    if (row.last_run_status === 'success') {
      return 100;
    }
    return 0;
  }

  private formatApiError(error: unknown, fallback: string): string {
    const err = error as { error?: { message?: string }; message?: string };
    return err?.error?.message || err?.message || fallback;
  }
}
